import React from 'react';
import { Check, Copy, Key, Loader2, RefreshCw } from 'lucide-react';
import { InfoStep } from '../SharedComponents';

const AccessCodesTab = ({ ctx }) => {
  const { dashboard, usage, accessCodes, regenerating, codeCopied, regenerateCode, copyCode } = ctx;

  // Compute activeCode from accessCodes
  const activeCode = accessCodes.find(c => c.active);

  return (
<div data-testid="access-codes-tab">
  <h1 className="text-xl font-bold mb-1" style={{ color: '#1A1209' }}>Parent Enrollment Codes</h1>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Share this code with parents so they can self-register and activate their child&apos;s HiBFF account.
  </p>

  {/* Active code card */}
  {activeCode ? (
    <div className="bg-white rounded-xl p-6 mb-6" style={{ border: '1px solid #E8E8E8' }} data-testid="active-code-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Active Code</h3>
        <span className="px-2.5 py-1 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#2EAA5C15', color: '#2EAA5C' }}>Active</span>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 rounded-xl p-4 text-center" style={{ backgroundColor: '#F8F7F4' }}>
          <p className="text-3xl font-bold tracking-[6px]" style={{ color: '#1A1209', fontFamily: 'monospace' }} data-testid="access-code-value">{activeCode.code}</p>
        </div>
        <button onClick={() => copyCode(activeCode.code)}
          className="px-4 py-3 rounded-lg text-sm font-medium flex items-center gap-1.5"
          style={{ backgroundColor: codeCopied ? '#2EAA5C' : '#F5831F', color: 'white' }}
          data-testid="copy-code-btn">
          {codeCopied ? <Check size={14} /> : <Copy size={14} />}
          {codeCopied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-lg p-3 text-center" style={{ backgroundColor: '#F8F7F4' }}>
          <p className="text-[10px] font-medium mb-1" style={{ color: '#999' }}>Created</p>
          <p className="text-xs font-semibold" style={{ color: '#333' }}>{new Date(activeCode.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
        </div>
        <div className="rounded-lg p-3 text-center" style={{ backgroundColor: '#F8F7F4' }}>
          <p className="text-[10px] font-medium mb-1" style={{ color: '#999' }}>Expires</p>
          <p className="text-xs font-semibold" style={{ color: '#333' }}>{new Date(activeCode.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
        </div>
        <div className="rounded-lg p-3 text-center" style={{ backgroundColor: '#F8F7F4' }}>
          <p className="text-[10px] font-medium mb-1" style={{ color: '#999' }}>Parents Enrolled</p>
          <p className="text-xs font-semibold" style={{ color: '#F5831F' }} data-testid="usage-count">{activeCode.usage_count || 0}</p>
        </div>
      </div>

      <button onClick={regenerateCode} disabled={regenerating}
        className="px-4 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
        style={{ backgroundColor: '#FEF2F2', color: '#DC2626' }}
        data-testid="regenerate-code-btn">
        {regenerating ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
        {regenerating ? 'Regenerating...' : 'Regenerate Code (deactivates current)'}
      </button>
    </div>
  ) : (
    <div className="bg-white rounded-xl p-6 mb-6 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <Key size={32} className="mx-auto mb-3" style={{ color: '#DDD' }} />
      <p className="text-sm mb-3" style={{ color: '#999' }}>No active enrollment code yet.</p>
      <button onClick={regenerateCode} disabled={regenerating}
        className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5 mx-auto disabled:opacity-50"
        style={{ backgroundColor: '#F5831F' }}
        data-testid="generate-first-code-btn">
        {regenerating ? <Loader2 size={14} className="animate-spin" /> : <Key size={14} />}
        Generate Enrollment Code
      </button>
    </div>
  )}

  {/* How it works */}
  <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E8E8E8' }}>
    <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>How Enrollment Codes Work</h3>
    <div className="space-y-3">
      <InfoStep num="1" text="Share your organisation's enrollment code with parents via email, letter, or your organisation portal." />
      <InfoStep num="2" text="Parents enter the code during HiBFF registration to link their child to your organisation." />
      <InfoStep num="3" text="Parents activate their child's account — students never register directly (COPPA compliant)." />
      <InfoStep num="4" text="You see aggregated engagement data on your dashboard. Individual conversations remain private." />
    </div>
  </div>

  {/* Previous codes */}
  {accessCodes.filter(c => !c.active).length > 0 && (
    <div className="mt-6">
      <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Previous Codes</h3>
      <div className="space-y-2">
        {accessCodes.filter(c => !c.active).map(c => (
          <div key={c.code} className="bg-white rounded-lg px-4 py-3 flex items-center justify-between" style={{ border: '1px solid #F0F0F0' }}>
            <span className="text-sm font-mono" style={{ color: '#AAA' }}>{c.code}</span>
            <div className="flex items-center gap-4">
              <span className="text-xs" style={{ color: '#CCC' }}>{c.usage_count || 0} uses</span>
              <span className="px-2 py-0.5 rounded-full text-[10px]" style={{ backgroundColor: '#F0F0F0', color: '#999' }}>Deactivated</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )}
</div>
  );
};

export default AccessCodesTab;
